import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { darken, rgba } from 'polished'

import { NavLink as RouterNavLink, NavLinkProps, useLocation } from 'react-router-dom'

import PerfectScrollbar from 'react-perfect-scrollbar'
import '../../../vendor/perfect-scrollbar.css'

import { spacing } from '@material-ui/system'

import {
  Box as MuiBox,
  Chip,
  Collapse,
  Drawer as MuiDrawer,
  List as MuiList,
  ListItem,
  ListItemText,
  Typography,
} from '@material-ui/core'

import { ExpandLess, ExpandMore } from '@material-ui/icons'

import { Layers } from 'react-feather'

import { menu } from './menu'

import useProfileContext from 'src/context/ProfileContext'

const NavLink = React.forwardRef((props: NavLinkProps, ref: React.Ref<HTMLAnchorElement>) => (
  <RouterNavLink innerRef={ref} {...props} />
))
NavLink.displayName = 'NavLink'

const Box = styled(MuiBox)(spacing)

const Drawer = styled(MuiDrawer)`
  border-right: 0;

  > div {
    border-right: 0;
  }
`

const Scrollbar = styled(PerfectScrollbar)`
  background-color: ${(props) => props.theme.sidebar.background};
  border-right: 1px solid rgba(0, 0, 0, 0.12);
`

const List = styled(MuiList)`
  background-color: ${(props) => props.theme.sidebar.background};
`

const Items = styled.div`
  padding-top: ${(props) => props.theme.spacing(2.5)}px;
  padding-bottom: ${(props) => props.theme.spacing(2.5)}px;
`

// There is a known issue with the material-ui ListItem and Typescript. This is
// the easiest work-around for now.
const Brand = styled(ListItem)<any>`
  font-size: ${(props) => props.theme.typography.h5.fontSize};
  font-weight: ${(props) => props.theme.typography.fontWeightMedium};
  color: ${(props) => props.theme.sidebar.header.color};
  background-color: ${(props) => props.theme.sidebar.header.background};
  font-family: ${(props) => props.theme.typography.fontFamily};
  min-height: 56px;
  padding-left: ${(props) => props.theme.spacing(6)}px;
  padding-right: ${(props) => props.theme.spacing(6)}px;

  ${(props) => props.theme.breakpoints.up('sm')} {
    min-height: 64px;
  }
`

const BrandIcon = styled(Layers)`
  margin-right: ${(props) => props.theme.spacing(2)}px;
  color: ${(props) => props.theme.sidebar.header.brand.color};
`

// There is a known issue with the material-ui ListItem and Typescript. This is
// the easiest work-around for now.
const Category = styled(ListItem)<any>`
  padding-top: ${(props) => props.theme.spacing(3)}px;
  padding-bottom: ${(props) => props.theme.spacing(3)}px;
  padding-left: ${(props) => props.theme.spacing(6)}px;
  padding-right: ${(props) => props.theme.spacing(5)}px;
  font-weight: ${(props) => props.theme.typography.fontWeightRegular};

  svg {
    color: ${(props) => props.theme.sidebar.color};
    font-size: 20px;
    width: 20px;
    height: 20px;
    opacity: 0.5;
  }

  &:hover {
    background: rgba(0, 0, 0, 0.08);
  }

  &.${(props) => props.activeClassName} {
    background-color: ${(props) => darken(0.05, props.theme.sidebar.background)};

    span {
      color: ${(props) => props.theme.sidebar.color};
    }
  }
`

const CategoryText = styled(ListItemText)`
  margin: 0;
  span {
    color: ${(props) => props.theme.sidebar.color};
    font-size: ${(props) => props.theme.typography.body1.fontSize}px;
    font-weight: ${(props) => props.theme.sidebar.category.fontWeight};
    padding: 0 ${(props) => props.theme.spacing(4)}px;
  }
`

const CategoryIconLess = styled(ExpandLess)`
  color: ${(props) => rgba(props.theme.sidebar.color, 0.5)};
`

const CategoryIconMore = styled(ExpandMore)`
  color: ${(props) => rgba(props.theme.sidebar.color, 0.5)};
`

interface LinkProps {
  activeClassName: string
  component: any
  exact?: boolean
  to: string
}

const Link = styled(ListItem)<LinkProps>`
  padding-left: ${(props) => props.theme.spacing(15)}px;
  padding-top: ${(props) => props.theme.spacing(2)}px;
  padding-bottom: ${(props) => props.theme.spacing(2)}px;

  span {
    color: ${(props) => rgba(props.theme.sidebar.color, 0.7)};
  }

  &:hover span {
    color: ${(props) => rgba(props.theme.sidebar.color, 0.9)};
  }

  &.${(props) => props.activeClassName} {
    background-color: ${(props) => darken(0.06, props.theme.sidebar.background)};

    span {
      color: ${(props) => props.theme.sidebar.color};
    }
  }
`

const LinkText = styled(ListItemText)`
  color: ${(props) => props.theme.sidebar.color};
  span {
    font-size: ${(props) => props.theme.typography.body1.fontSize}px;
  }
  margin-top: 0;
  margin-bottom: 0;
`

const LinkBadge = styled(Chip)`
  font-size: 11px;
  font-weight: ${(props) => props.theme.typography.fontWeightBold};
  height: 20px;
  position: absolute;
  right: 12px;
  top: 8px;
  background: ${(props) => props.theme.sidebar.badge.background};

  span.MuiChip-label,
  span.MuiChip-label:hover {
    cursor: pointer;
    color: ${(props) => props.theme.sidebar.badge.color};
    padding-left: ${(props) => props.theme.spacing(2)}px;
    padding-right: ${(props) => props.theme.spacing(2)}px;
  }
`

const CategoryBadge = styled(LinkBadge)`
  top: 12px;
`

const SidebarSection = styled(Typography)`
  color: ${(props) => props.theme.sidebar.color};
  padding: ${(props) => props.theme.spacing(4)}px ${(props) => props.theme.spacing(6)}px
    ${(props) => props.theme.spacing(1)}px;
  opacity: 0.9;
  display: block;
`

interface SidebarCategoryProps {
  name: string
  to?: string
  activeClassName?: string
  component?: any
  icon: JSX.Element
  classes?: string
  isOpen?: boolean
  isCollapsable: boolean
  badge?: string
  exact?: boolean
  button?: boolean
  onClick?: () => void
}

const SidebarCategory = (props: SidebarCategoryProps) => {
  const { name, icon, isOpen, isCollapsable, badge, activeClassName, to, button, onClick } = props
  return (
    <Category
      activeClassName={activeClassName}
      component={to ? NavLink : null}
      to={to}
      button={button}
      onClick={onClick}
    >
      {icon}
      <CategoryText>{name}</CategoryText>
      {isCollapsable ? isOpen ? <CategoryIconMore /> : <CategoryIconLess /> : null}
      {badge ? <CategoryBadge label={badge} /> : ''}
    </Category>
  )
}

interface SidebarLinkProps {
  name: string
  to: string
  icon?: JSX.Element
  badge?: string
}

const SidebarLink = (props: SidebarLinkProps) => {
  const { name, to, badge } = props
  return (
    <Link button dense component={NavLink} exact to={to} activeClassName="active">
      <LinkText>{name}</LinkText>
      {badge ? <LinkBadge label={badge} /> : ''}
    </Link>
  )
}

interface SidebarProps {
  classes?: string
  staticContext?: any
  routes: any // TODO: Create Routes Interface
  PaperProps: any // TODO: Create PaperProps Interface
  variant?: 'permanent' | 'persistent' | 'temporary'
  open?: boolean
  onClose?: () => void
}

export const ClientSidebar = (props: SidebarProps) => {
  const { open, onClose, variant, PaperProps } = props
  const location = useLocation()
  const initOpenCategories = () => {
    /* Open collapse element that matches current url */
    const pathName = location.pathname

    const _categories: { [key: string]: boolean } = {}

    menu.forEach((route) => {
      const isActive = pathName.indexOf(route.path) === 0
      const isOpen = route.open
      const isHome = route.containsHome && pathName === '/'

      _categories[route.id] = isActive || isOpen || isHome
    })

    return _categories
  }


  const [openCategories, setOpenCategories] = useState(() => initOpenCategories())
  const profileContext = useProfileContext()
  useEffect(() => {
    profileContext.updateProfile()
  }, [profileContext])

  const toggle = (key: string) => {
    // Collapse all elements
    Object.keys(openCategories).forEach(
      (item) =>
        openCategories[key] ||
        setOpenCategories((openCategories) => Object.assign({}, openCategories, { [item]: false }))
    )

    // Toggle selected element
    setOpenCategories((openCategories) =>
      Object.assign({}, openCategories, { [key]: !openCategories[key] })
    )
  }

  return (
    <Drawer variant={variant || 'permanent'} open={open} onClose={onClose} PaperProps={PaperProps}>
      <Brand>
        <BrandIcon /> <Box ml={1}>SKUit</Box>
      </Brand>
      <Scrollbar>
        <List disablePadding>
          <Items>
            {menu.map((category) => (
              <React.Fragment key={category.id}>
                {category.header ? <SidebarSection>{category.header}</SidebarSection> : null}

                {category.children ? (
                  <>
                    <SidebarCategory
                      isOpen={!openCategories[category.id]}
                      isCollapsable={true}
                      name={category.id}
                      icon={category.icon}
                      button={true}
                      onClick={() => toggle(category.id)}
                    />

                    <Collapse in={openCategories[category.id]} timeout="auto" unmountOnExit>
                      {category.children.map((route, index) => (
                        <SidebarLink
                          key={index}
                          name={route.name}
                          to={route.path}
                          icon={route.icon}
                          badge={route.badge}
                        />
                      ))}
                    </Collapse>
                  </>
                ) : (
                  <SidebarCategory
                    isCollapsable={false}
                    name={category.id}
                    to={category.path}
                    activeClassName="active"
                    component={NavLink}
                    icon={category.icon}
                    exact
                    badge={category.badge}
                  />
                )}
              </React.Fragment>
            ))}
          </Items>
        </List>
      </Scrollbar>
    </Drawer>
  )
}
